import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import { Ability, subject } from '@casl/ability';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../shared/components';
import { appDataDetails } from '../../../../../../../../shared/core/shared.selectors';
import { ProjectsService } from '../../../../../../core/projects.service';
import { projectAccountsInfo, projectDetails } from '../../../../../../core/projects.selectors';
import { FetchProjectAccountsRequest } from '../../../../../../core/projects.actions';
import { MgvwRecordsService } from '../mgvw-records.service';
import {
  MGVW_STATUS_MAP,
  MgvwField,
  MgvwPickListOption,
  MgvwSection,
  buildFormSeed,
  buildLookupBag,
  buildPayloadValues,
  buildSections,
  coerceExpressionValue,
  hasPickList,
  isDisplayOnly,
  isMgvwReadOnly,
  isRequired,
  mgvwStatusKey,
  missingRequiredLabels,
  newClientUuid,
  parseMgvwDate,
  recomputeDerivedValues,
  resolvePickListOptions,
  sortFields,
} from '../mgvw-form.util';

/**
 * Create / view / edit one MGVW (Maximum Gross Vehicle Weight) record in
 * the project page's right panel. The form is the org's `contract_mgvw`
 * custom_group off `appData.dailyReportFields`, rendered section by section
 * the way mobile's MgvwEntryScreen does, with the allowable weights
 * recomputed live from the config expressions on every edit.
 *
 * Draft records save with PUT/POST; "Submit to AASHTOWare" saves first when
 * needed, then POSTs /submit. Submitted records (and records the inbound
 * sync brought over from AASHTOWare) are read-only, matching the api's 422.
 */
@Component({
  selector: 'app-create-or-edit-mgvw-record',
  templateUrl: './create-or-edit-mgvw-record.component.html',
  styleUrls: ['./create-or-edit-mgvw-record.component.scss'],
  standalone: false,
})
export class CreateOrEditMgvwRecordComponent implements OnInit, OnDestroy {
  @Input() data: any = {};
  @Input() options: any = {};

  projectId: string;
  /** The live row: the list row on open, the api response after every save. */
  record: any = null;
  saving = false;
  submitting = false;
  downloading = false;
  errorMessage: string | null = null;
  /** True when the org has no contract_mgvw group yet (the api 422s creates too). */
  notConfigured = false;

  sections: MgvwSection[] = [];
  fields: MgvwField[] = [];
  form = new FormGroup<any>({});
  pickListOptions: Record<string, MgvwPickListOption[]> = {};
  expandedSections: Record<string, boolean> = {};
  canUpdate = false;

  private appData: any = null;
  private projectData: any = null;
  private projectAccounts: any[] = [];
  private lookups: Record<string, any[]> = {};
  /** The client uuid sent on the first POST; the api dedupes on it. */
  private clientUuid: string = null;
  private formBuilt = false;
  private recomputing = false;
  private readonly onDestroy = new Subject<void>();

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private cdRef: ChangeDetectorRef,
    private commonService: CommonService,
    private projectsService: ProjectsService,
    private mgvwRecordsService: MgvwRecordsService,
    private ability: Ability,
  ) {}

  ngOnInit(): void {
    this.projectId = this.resolveProjectId();
    this.record = this.data?.uuid ? { ...this.data } : null;
    if (this.projectId) {
      this.canUpdate = this.ability.can('update', subject('MgvwRecord', { project_id: this.projectId }));
    }

    this.store
      .select(appDataDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        this.appData = data;
        this.lookups = buildLookupBag(data?.lookups, this.projectData?.lookups);
        if (this.projectId) {
          this.canUpdate = this.ability.can('update', subject('MgvwRecord', { project_id: this.projectId }));
        }
        if (!this.formBuilt) {
          this.buildForm();
        } else {
          this.recomputePickListOptions();
        }
      });

    this.store
      .select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((project: any) => {
        this.projectData = project;
        this.lookups = buildLookupBag(this.appData?.lookups, project?.lookups);
        if (this.formBuilt) {
          this.recomputePickListOptions();
          this.recomputeDerived();
        }
      });

    this.store
      .select(projectAccountsInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((info: any) => {
        this.projectAccounts = Array.isArray(info?.list) ? info.list : [];
        if (this.formBuilt) this.recomputePickListOptions();
      });
  }

  get readOnly(): boolean {
    return isMgvwReadOnly(this.record) || (!!this.record && !this.canUpdate);
  }

  get isNew(): boolean {
    return !this.record?.uuid;
  }

  get title(): string {
    const truckId = this.form?.get('truck_id')?.value || this.record?.values?.truck_id;
    return truckId || (this.isNew ? 'New MGVW record' : 'MGVW record');
  }

  /** Chip classes per status, shared with the list. */
  statusMap = MGVW_STATUS_MAP;

  get statusKey(): string {
    return mgvwStatusKey(this.record);
  }

  private buildForm(): void {
    const group = this.appData?.dailyReportFields?.contract_mgvw;
    if (!group?.fields?.length) {
      this.notConfigured = !!this.appData;
      return;
    }
    this.notConfigured = false;
    this.fields = sortFields(group.fields);
    this.sections = buildSections(group);

    const seed = recomputeDerivedValues(this.fields, buildFormSeed(this.fields, this.record?.values || {}), this.lookups);
    const controls: Record<string, FormControl> = {};
    this.fields.forEach(field => {
      const validators = isRequired(field) && !isDisplayOnly(field) ? [Validators.required] : [];
      controls[field.name] = new FormControl(seed[field.name] ?? null, validators);
    });
    this.form = new FormGroup(controls);
    this.formBuilt = true;
    if (this.readOnly) {
      this.form.disable({ emitEvent: false });
    }

    // First section open, the rest collapsed; a read-only record opens all.
    this.sections.forEach((section, idx) => {
      this.expandedSections[section.key] = this.readOnly || idx === 0;
    });

    if (this.fields.some(f => f?.ui_config?.pick_list === 'project_staff') && this.projectId) {
      this.store.dispatch(FetchProjectAccountsRequest({ payload: { projectId: this.projectId } }));
    }
    this.recomputePickListOptions();

    this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.recomputeDerived());
  }

  private recomputePickListOptions(): void {
    const ctx = {
      lookups: this.lookups,
      subContractors: this.projectData?.sub_contractors || [],
      accounts: this.projectAccounts,
    };
    const next: Record<string, MgvwPickListOption[]> = {};
    this.fields.filter(hasPickList).forEach(field => {
      const options = resolvePickListOptions(field, ctx);
      // Keep a stored value selectable even when it left the source list
      // (e.g. a contractor removed from the project after the record was saved).
      const current = this.form.get(field.name)?.value;
      if (current !== null && current !== undefined && current !== '' && !options.some(o => o.value === String(current))) {
        options.unshift({ label: String(current), value: String(current) });
      }
      next[field.name] = options;
    });
    this.pickListOptions = next;
    this.cdRef.markForCheck();
  }

  /**
   * Re-evaluates every expression / lookup field and writes back only the
   * ones that changed, without emitting (so this never loops). Editable
   * fields with an expression (inch tread width) update too.
   */
  private recomputeDerived(): void {
    if (this.recomputing || !this.formBuilt) return;
    this.recomputing = true;
    try {
      const raw = this.form.getRawValue();
      const next = recomputeDerivedValues(this.fields, raw, this.lookups);
      this.fields.forEach(field => {
        const cfg = field?.ui_config || {};
        if (!cfg.expression && !cfg.lookup_attr) return;
        const control = this.form.get(field.name);
        if (!control) return;
        const before = coerceExpressionValue(control.value);
        const after = coerceExpressionValue(next[field.name]);
        if (before !== after) {
          control.setValue(next[field.name] ?? null, { emitEvent: false });
        }
      });
    } finally {
      this.recomputing = false;
    }
  }

  toggleSection(key: string): void {
    this.expandedSections[key] = !this.expandedSections[key];
  }

  isSectionExpanded(key: string): boolean {
    return !!this.expandedSections[key];
  }

  isDisplayOnly(field: MgvwField): boolean {
    return isDisplayOnly(field);
  }

  hasPickList(field: MgvwField): boolean {
    return hasPickList(field);
  }

  isRequired(field: MgvwField): boolean {
    return isRequired(field);
  }

  isBoolean(field: MgvwField): boolean {
    return field?.type === 'boolean';
  }

  isDate(field: MgvwField): boolean {
    return field?.type === 'date';
  }

  isNumber(field: MgvwField): boolean {
    return field?.type === 'number';
  }

  /** The read-only rendering of a field's current value. */
  displayValue(field: MgvwField): string {
    const value = this.form.get(field.name)?.value;
    if (value === null || value === undefined || value === '') return '-';
    if (this.isBoolean(field)) return value === true || value === 'true' ? 'Yes' : 'No';
    if (this.isDate(field)) {
      const parsed = parseMgvwDate(value);
      return parsed ? parsed.format('MMM D, YYYY') : String(value);
    }
    if (this.hasPickList(field)) {
      const hit = (this.pickListOptions[field.name] || []).find(o => o.value === String(value));
      return hit?.label || String(value);
    }
    if (this.isNumber(field) || typeof value === 'number') {
      const n = Number(value);
      return Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 3 }) : String(value);
    }
    return String(value);
  }

  private validateRequired(): boolean {
    const missing = missingRequiredLabels(this.fields, this.form.getRawValue());
    if (!missing.length) return true;
    this.form.markAllAsTouched();
    this.errorMessage = `Please fill the required field(s): ${missing.join(', ')}`;
    this.commonService.notification(this.errorMessage, 'error');
    return false;
  }

  private persist(): Observable<any> {
    const values = buildPayloadValues(this.fields, this.form.getRawValue());
    if (this.record?.uuid) {
      return this.mgvwRecordsService.update(this.record.uuid, values);
    }
    if (!this.clientUuid) this.clientUuid = newClientUuid();
    return this.mgvwRecordsService.create({
      project_uuid: this.projectId,
      values,
      uuid: this.clientUuid,
    });
  }

  private absorb(resp: any): void {
    const rec = resp?.data || resp;
    if (rec?.uuid) {
      this.record = { ...(this.record || {}), ...rec };
      this.data = { ...this.data, ...rec };
    }
  }

  save(): void {
    if (this.saving || this.submitting || this.readOnly) return;
    if (!this.validateRequired()) return;
    this.saving = true;
    this.errorMessage = null;
    this.persist()
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: resp => {
          this.saving = false;
          this.absorb(resp);
          this.form.markAsPristine();
          this.commonService.notification('MGVW record saved', 'success');
          this.mgvwRecordsService.refresh.next();
          if (this.record?.uuid) {
            this.router.navigate([], { queryParams: { mgvwId: this.record.uuid }, queryParamsHandling: 'merge' });
          }
        },
        error: err => {
          this.saving = false;
          this.errorMessage = err?.error?.message || err?.message || 'Save failed';
          this.commonService.notification(this.errorMessage, 'error');
        },
      });
  }

  /**
   * One confirmation, then: save when the record is new or edited, POST
   * /submit, and lock the form once AASHTOWare has the instance.
   */
  submit(): void {
    if (this.saving || this.submitting || this.readOnly) return;
    if (!this.validateRequired()) return;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Submit to AASHTOWare',
        'This pushes the record to AASHTOWare and makes it read-only in SiteNotes.<br/>Continue?',
        'Submit',
        'Cancel',
      ),
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.submitting = true;
      this.errorMessage = null;

      const afterSave = () => {
        this.mgvwRecordsService
          .submit(this.record.uuid)
          .pipe(takeUntil(this.onDestroy))
          .subscribe({
            next: resp => {
              this.submitting = false;
              this.absorb(resp);
              this.record = { ...this.record, status: 'submitted' };
              this.form.markAsPristine();
              this.form.disable({ emitEvent: false });
              this.sections.forEach(s => (this.expandedSections[s.key] = true));
              this.commonService.notification('Submitted to AASHTOWare', 'success');
              this.mgvwRecordsService.refresh.next();
            },
            error: err => {
              this.submitting = false;
              this.errorMessage =
                err?.error?.error || err?.error?.message || err?.message
                || 'Submit failed. The record stays a Draft, please try again.';
              this.commonService.notification(this.errorMessage, 'error');
              // The draft did save; make sure the list shows it.
              this.mgvwRecordsService.refresh.next();
            },
          });
      };

      if (this.record?.uuid && !this.form.dirty) {
        afterSave();
        return;
      }
      this.persist()
        .pipe(takeUntil(this.onDestroy))
        .subscribe({
          next: resp => {
            this.absorb(resp);
            if (!this.record?.uuid) {
              this.submitting = false;
              this.errorMessage = 'Save did not return a record';
              this.commonService.notification(this.errorMessage, 'error');
              return;
            }
            afterSave();
          },
          error: err => {
            this.submitting = false;
            this.errorMessage = err?.error?.message || err?.message || 'Save failed';
            this.commonService.notification(this.errorMessage, 'error');
          },
        });
    });
  }

  viewPdf(): void {
    if (!this.record?.uuid || this.downloading) return;
    this.downloading = true;
    this.mgvwRecordsService
      .downloadPdf(this.record.uuid)
      .catch(err => this.commonService.notification(err?.message || 'Could not download the PDF', 'error'))
      .finally(() => {
        this.downloading = false;
        this.cdRef.markForCheck();
      });
  }

  close(): void {
    this.projectsService.closeRightPanel.next();
  }

  submittedOn(): string {
    const ts = this.record?.submitted_at;
    return ts && moment(ts).isValid() ? moment(ts).format('MMM D, YYYY') : '';
  }

  trackByName(_idx: number, item: any): string {
    return item?.name || item?.key;
  }

  /**
   * Instantiated dynamically via `projectsService.openRightPanel`, so the
   * ActivatedRoute sits above the `:projectId` segment. Walk the tree, then
   * fall back to the url (same as the sample-record panel).
   */
  private resolveProjectId(): string | undefined {
    const fromTree = (r: ActivatedRoute | null): string | undefined => {
      if (!r) return undefined;
      const v = r.snapshot.paramMap.get('projectId');
      if (v) return v;
      for (const c of r.children) {
        const found = fromTree(c);
        if (found) return found;
      }
      return undefined;
    };
    let id = fromTree(this.route);
    if (!id) {
      let root: ActivatedRoute = this.route;
      while (root.parent) root = root.parent;
      id = fromTree(root);
    }
    if (!id) {
      const m = (this.router?.url || '').match(/\/projects\/([^/?#]+)/);
      if (m) id = m[1];
    }
    return id;
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
